/**
 * OneOnOneSessionDetailDrawer — E18-3
 * 80%-width side-pane for 1:1 coaching session details.
 * Matches the existing group coaching SessionDetailDrawer 3-tab pattern:
 *   Pre Session (RSVPs + Resources)
 *   Post Session (Attendance + Coach Feedback — no Participant Feedback for 1:1)
 *   Internal Notes
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Drawer,
  Tabs,
  Badge,
  ScrollArea,
  Group,
  Avatar,
  Text,
  ActionIcon,
  Box,
  Stack,
  ThemeIcon,
  Anchor,
  Center,
  Loader,
  Button,
} from '@mantine/core';
import { Link, useLocation } from 'react-router-dom';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { Calendar, Clipboard, FileText, X, Video } from 'react-feather';
import { cohortsApi } from '../../../../services/api/cohorts.js';
import { sessionsApi, type SessionWithDetails } from '../../../../services/api/sessions.js';
import { isCheckinOneOnOneType } from '../../../../types';
import type { MissClaim, OneOnOneType, ResourceCategory } from '../../../../types';
import { missClaimsApi } from '../../../../services/api/missClaims';
import {
  asksLateChangeQuestion,
  labelClaim,
  missLabel,
  oneOnOneHistoryHref,
} from '../../../../components/coaches/missClaimText';
import { MissTagTooltip } from '../../../../components/coaches/MissTagTooltip';
import { openMissClaimQuestion } from '../../../../components/coaches/missClaimQuestion';
import { FlagLateChangeModal } from './FlagLateChangeModal';
import { getOneOnOneTypeConfig } from '../../../../config/oneOnOneTypeConfig';
import { PreSessionTab } from './PreSessionTab.js';
import { InternalNotesSection } from './InternalNotesSection.js';
import { SectionAccordion } from './SectionAccordion.js';
import { AttendanceSection } from './AttendanceSection.js';
import { CoachFeedbackSection } from './CoachFeedbackSection.js';
import { isAbsentAttendance, isPastAwaitingAttendance } from './sessionsHelpers';
import styles from './SessionDetailDrawer.module.css';
import { getZoneCode } from '../../../../utils/formatters';

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// MED-1350: show the end time too: "Tue, Oct 7, 2:00 PM to 2:20 PM ET".
function formatHeaderDate(
  dateStr: string | null,
  endStr: string | null,
  tz: string | null,
): string {
  if (!dateStr) return '';
  try {
    const timeZone = tz || 'America/New_York';
    const date = new Date(dateStr);
    if (endStr) {
      const startPart = new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
      }).format(date);
      const end = new Date(endStr);
      const endPart = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
      }).format(end);
      return `${startPart} to ${endPart} ${getZoneCode(timeZone, end)}`;
    }
    const startPart = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    }).format(date);
    return `${startPart} ${getZoneCode(timeZone, date)}`;
  } catch {
    return dateStr;
  }
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  unscheduled: { label: 'Unscheduled', color: 'gray' },
  scheduled: { label: 'Scheduled', color: 'blue' },
  in_progress: { label: 'In Progress', color: 'orange' },
  completed: { label: 'Completed', color: 'green' },
  cancelled: { label: 'Cancelled', color: 'red' },
};

/** MED-1360: a scheduled session past its time with no attendance. */
const NEEDS_ATTENDANCE_STATUS = { label: 'Needs attendance', color: 'orange' };

/** MED-1346 F13: the participant was absent or excused. The row status stays 'completed'. */
const ABSENT_STATUS = { label: 'Absent', color: 'red' };

interface OneOnOneSessionDetailDrawerProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  sessionId: string;
  /** Participant's full name — used in header title */
  participantName: string;
  participantAvatarUrl: string | null;
  /** The participant's 1:1 coach. Shown only when the session has no coach. */
  coachName: string | null;
  coachAvatarUrl: string | null;
  categories?: ResourceCategory[];
  onComplete?: () => void;
  /** E22-5d: total number of 1:1 sessions for the participant (for "Session X of Y" header) */
  totalSessions?: number;
  /**
   * MED-1350: opens the Edit Session modal for this session (date edit and
   * Clear date). Omit it for a viewer who cannot edit dates.
   */
  onEditDate?: (session: SessionWithDetails) => void;
  /**
   * MED-1350: true when this viewer may edit this session's date. The coach
   * page passes the server's owner rule. Omit it to allow every session.
   */
  canEditDate?: (session: SessionWithDetails) => boolean;
  /**
   * MED-1360: true when this viewer coaches this session, so they may mark
   * attendance, see the session notes and add internal notes. The server
   * refuses all three for any other coach. The coach page passes the
   * server's owner rule. Omit it to allow every session (staff view).
   */
  canActOnSession?: (session: SessionWithDetails) => boolean;
  /** MED-1360: hides Add Resource (the coach page; see PreSessionTab). */
  resourcesReadOnly?: boolean;
  /**
   * MED-1346 F13: the participant's attendance status on this 1:1, from the
   * live 1:1 list. Absent or excused shows "Absent", never "Completed".
   */
  attendanceStatus?: string | null;
  /** MED-1346 F14: the participant's user id, for the staff "See 1:1 history" link. */
  participantUserId?: string;
}

/** MED-1350: a 1:1 in these states keeps its date. */
const DATE_LOCKED_STATUSES = new Set(['completed', 'cancelled', 'in_progress']);

export function OneOnOneSessionDetailDrawer({
  opened,
  onClose,
  cohortId,
  sessionId,
  participantName,
  participantAvatarUrl,
  coachName,
  coachAvatarUrl,
  categories = [],
  onComplete,
  totalSessions,
  onEditDate,
  canEditDate,
  canActOnSession,
  resourcesReadOnly = false,
  attendanceStatus,
  participantUserId,
}: OneOnOneSessionDetailDrawerProps) {
  const [fullSession, setFullSession] = useState<SessionWithDetails | null>(null);
  // MED-1360: the session id whose fetch failed, so the owner stays unknown.
  const [loadFailedId, setLoadFailedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('pre_session');
  const [notesCount, setNotesCount] = useState(0);
  // null: not loaded yet, or the load failed.
  const [resourceCount, setResourceCount] = useState<number | null>(null);
  // MED-1393: hides both date links while a cancel is in flight.
  const [cancelling, setCancelling] = useState(false);
  // MED-1346: the 1:1's miss claims, newest first, and its paid miss count.
  // null: not loaded, or this viewer may not read them.
  const [missClaims, setMissClaims] = useState<{ claims: MissClaim[]; paidMissCount: number } | null>(
    null,
  );
  const [flagOpened, setFlagOpened] = useState(false);
  // MED-1346: CM-only actions. activePersona stays admin for coach logins and
  // impersonation, so the /coach/ route tells the coach view apart.
  const isCoachView = useLocation().pathname.startsWith('/coach/');
  const [sessionData, setSessionData] = useState<{
    session_number: number | null;
    scheduled_start: string | null;
    scheduled_end: string | null;
    timezone: string | null;
    status: string;
    zoom_link: string | null;
    video_link: string | null;
    attendance_marked?: boolean;
    feedback_submitted?: boolean;
    one_on_one_type?: OneOnOneType | null;
    coach_id: string | null;
    session_coach_name: string | null;
    session_coach_avatar_url: string | null;
    had_earlier_start: boolean;
  } | null>(null);

  // MED-1360: a late response for a session the drawer no longer shows must
  // not replace the current one.
  const currentSessionIdRef = useRef(sessionId);
  currentSessionIdRef.current = sessionId;

  const fetchSessionData = useCallback(async () => {
    try {
      const session = await sessionsApi.getById(cohortId, sessionId);
      if (currentSessionIdRef.current !== sessionId) return;
      setLoadFailedId(null);
      setFullSession(session);
      setSessionData({
        session_number: session.session_number,
        scheduled_start: session.scheduled_start,
        scheduled_end: session.scheduled_end ?? null,
        timezone: session.timezone || null,
        status: session.status,
        zoom_link: (session as { zoom_link?: string | null }).zoom_link ?? null,
        video_link: (session as { video_link?: string | null }).video_link ?? null,
        attendance_marked: session.attendance_marked,
        feedback_submitted: (session as { feedback_submitted?: boolean }).feedback_submitted,
        one_on_one_type: session.one_on_one_type ?? null,
        coach_id: session.coach_id ?? null,
        session_coach_name: session.session_coach_name ?? null,
        session_coach_avatar_url: session.session_coach_avatar_url ?? null,
        had_earlier_start: !!session.had_earlier_start,
      });
    } catch {
      // silent — session may not exist yet
      if (currentSessionIdRef.current !== sessionId) return;
      setLoadFailedId(sessionId);
    }
  }, [cohortId, sessionId]);

  const fetchMissClaims = useCallback(async () => {
    try {
      const data = await missClaimsApi.listForSession(cohortId, sessionId);
      if (currentSessionIdRef.current !== sessionId) return;
      setMissClaims({
        claims: [...data.claims].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
        paidMissCount: data.paidMissCount,
      });
    } catch {
      // Another coach's 1:1 answers 403. The drawer then shows no miss label.
      if (currentSessionIdRef.current !== sessionId) return;
      setMissClaims(null);
    }
  }, [cohortId, sessionId]);

  useEffect(() => {
    if (opened) {
      // Set default tab based on status
      const isCompleted = sessionData?.status === 'completed';
      setActiveTab(isCompleted ? 'post_session' : 'pre_session');
      setNotesCount(0);
      setResourceCount(null);
      setMissClaims(null);
      fetchSessionData();
      fetchMissClaims();
    }
  }, [opened, cohortId, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // MED-1360: past its time with no attendance. The header says so, and the
  // drawer opens on Post Session, where attendance is marked.
  const needsAttendance = !!sessionData && isPastAwaitingAttendance(sessionData);

  // Re-derive default tab once session data loads. MED-1393: also on each open,
  // because a reopen fetches the same data and the values above do not change.
  useEffect(() => {
    if (sessionData && opened) {
      setActiveTab(
        sessionData.status === 'completed' || needsAttendance ? 'post_session' : 'pre_session',
      );
    }
  }, [opened, sessionData?.status, needsAttendance]); // eslint-disable-line react-hooks/exhaustive-deps

  // MED-1393: a coach cannot add resources and a 1:1 has no RSVPs, so with no
  // resources the coach's Pre Session tab is empty. Hide it.
  const showPreSession = !resourcesReadOnly || resourceCount !== 0;
  const shownTab = !showPreSession && activeTab === 'pre_session' ? 'post_session' : activeTab;

  const handleNotesChanged = useCallback((count: number) => {
    setNotesCount(count);
  }, []);

  const handleComplete = useCallback(() => {
    fetchSessionData();
    fetchMissClaims();
    onComplete?.();
  }, [fetchSessionData, fetchMissClaims, onComplete]);

  const sessionNumber = sessionData?.session_number;
  const typeConfig = getOneOnOneTypeConfig(sessionData?.one_on_one_type);
  const isCheckin = isCheckinOneOnOneType(sessionData?.one_on_one_type);
  // MED-1346: check-ins never have misses. The label claim follows the API latestMiss rule.
  const shownClaim = labelClaim(missClaims?.claims);
  const missBadge = !isCheckin && shownClaim
    ? missLabel(
        {
          ...shownClaim,
          noShowCount: missClaims!.claims.filter(c => c.kind === 'no_show').length,
        },
        sessionData?.scheduled_start
      )
    : null;
  // F8: an undated 1:1 has nothing to mark. A no-show reopens the 1:1 and resets
  // its attendance, so the drawer offers Add date instead of the attendance form.
  const undated = !!sessionData && !sessionData.scheduled_start && !sessionData.attendance_marked;
  // F14: staff reach the 1:1 history from every 1:1, with or without a miss.
  const historyParticipantId = participantUserId ?? shownClaim?.participantUserId;
  // R6 (tester ruling, comment 13063): never on a 1:1 with a future date, even a moved
  // one; staff flag a late move once the new date passes. An undated 1:1 shows it only
  // when it had an earlier start (cleared or canceled).
  const scheduledStart = sessionData?.scheduled_start;
  const canFlagLateChange =
    !isCoachView &&
    !!sessionData &&
    !isCheckin &&
    (scheduledStart
      ? new Date(scheduledStart).getTime() <= Date.now()
      : !!sessionData.had_earlier_start);
  // AC12: Header shows "Session X of Y · {Full Label} · {participant}".
  // MED-1350: a check-in has no number: "Intro check-in · {participant}".
  const sessionLabel = !isCheckin && sessionNumber != null
    ? (totalSessions && totalSessions > 0
        ? `Session ${sessionNumber} of ${totalSessions}`
        : `Session ${sessionNumber}`)
    : null;
  // AC8: Full type label in header
  const title = [
    sessionLabel,
    isCheckin ? typeConfig.displayLabel : typeConfig.fullLabel,
    participantName || null,
  ]
    .filter(Boolean)
    .join(' · ');

  const statusConfig = isAbsentAttendance(attendanceStatus)
    ? ABSENT_STATUS
    : needsAttendance
    ? NEEDS_ATTENDANCE_STATUS
    : STATUS_CONFIG[sessionData?.status ?? 'unscheduled'] ?? STATUS_CONFIG.unscheduled;
  // MED-1350: the header shows the session's own coach for every 1:1. A
  // check-in row takes the group coach first, so it can differ from the
  // participant's 1:1 coach. Fall back to the participant's coach only when
  // the session has no coach (or before the session loads).
  const sessionHasCoach = !!sessionData?.coach_id && !!sessionData.session_coach_name;
  const headerCoachName = sessionHasCoach ? sessionData!.session_coach_name : coachName;
  const headerCoachAvatarUrl = sessionHasCoach
    ? sessionData!.session_coach_avatar_url
    : coachAvatarUrl;
  const zoomLink = sessionData?.video_link ?? sessionData?.zoom_link;

  // MED-1360: a coach who does not coach this session sees it read-only. The
  // attendance and coach feedback reads are not even sent, because the server
  // answers 403 and a Retry can never pass. Until the session loads, the
  // owner is unknown, so nothing is sent and nothing is offered. If the fetch
  // fails, the owner stays unknown: attendance then loads by itself as before,
  // and the server refuses a non-owner (error with Retry).
  const sessionLoaded = fullSession?.id === sessionId;
  const sessionLoadFailed = loadFailedId === sessionId;
  const ownerKnown = !canActOnSession || sessionLoaded || sessionLoadFailed;
  const viewerActs = !canActOnSession || (sessionLoaded && canActOnSession(fullSession));
  // MED-1346: the session coach or staff answers a pending late change here too,
  // with the same question as the dashboard card.
  const pendingClaim = isCheckin
    ? undefined
    : missClaims?.claims.find(c => c.decision === 'pending');
  const canAnswerClaim = !!pendingClaim && (!isCoachView || viewerActs);

  // MED-1350: the same gate as Edit date (partner coaches pass no onEditDate).
  const canChangeDate =
    !!onEditDate &&
    !!fullSession &&
    sessionLoaded &&
    (!canEditDate || canEditDate(fullSession)) &&
    !!sessionData &&
    !DATE_LOCKED_STATUSES.has(sessionData.status) &&
    !cancelling;

  // MED-1393: cancel is Clear date by its plain name. The server cancels the
  // Calendly booking or deletes the calendar invite (with notices), and the
  // row goes back to unscheduled so the participant can book again.
  const handleCancelSession = () =>
    modals.openConfirmModal({
      title: 'Cancel this session?',
      children: (
        <Text size="sm">
          The session goes back to unscheduled, and its booking or calendar invite is canceled.
          {participantName ? ` ${participantName}` : ' The participant'} can then book a new time.
        </Text>
      ),
      labels: { confirm: 'Cancel session', cancel: 'Keep session' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setCancelling(true);
        try {
          // F12: a cancel, so the history says "Canceled", not "Date cleared".
          const cleared = await cohortsApi.clearSessionDate(cohortId, sessionId, { cancel: true });
          notifications.show({
            title: 'Session canceled',
            message: 'The session is unscheduled again.',
            color: 'green',
          });
          // Await the reload so the links stay hidden until the status changes.
          await fetchSessionData();
          fetchMissClaims();
          onComplete?.();
          // MED-1346: a cancel within 24 hours asks the coach about the notice.
          // F12: a cancel after the start never asks.
          if (isCoachView && asksLateChangeQuestion(cleared.missClaim)) {
            openMissClaimQuestion(cleared.missClaim, handleComplete);
          }
        } catch (error) {
          notifications.show({
            title: 'Could not cancel the session',
            message: error instanceof Error ? error.message : 'Failed to cancel the session',
            color: 'red',
          });
        } finally {
          setCancelling(false);
        }
      },
    });

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="80%"
      padding={0}
      withCloseButton={false}
      styles={{
        body: { height: '100%', display: 'flex', flexDirection: 'column' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <Box
        style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--mantine-color-gray-2)',
          flexShrink: 0,
        }}
      >
        <Group wrap="nowrap" align="flex-start">
          {/* The people chips wrap under the title on a narrow drawer, so
              the title and date keep their width; Close stays top right. */}
          <Group
            justify="space-between"
            align="flex-start"
            gap="md"
            style={{ flex: 1, minWidth: 0 }}
          >
            {/* Left: title + date + status + meeting link */}
            <Stack gap={4} style={{ flex: '1 1 240px', minWidth: 0 }}>
              <Text fw={700} size="lg" style={{ lineHeight: 1.2 }}>
                {title || 'Session'}
              </Text>
              {sessionData?.scheduled_start && (
                <Group gap={8} align="center">
                  <Text size="sm" c="dimmed">
                    {formatHeaderDate(
                      sessionData.scheduled_start,
                      sessionData.scheduled_end,
                      sessionData.timezone,
                    )}
                  </Text>
                  {/* MED-1350: reach the date edit and Clear date. */}
                  {canChangeDate && (
                    <Anchor
                      component="button"
                      type="button"
                      size="sm"
                      onClick={() => onEditDate!(fullSession!)}
                    >
                      Edit date
                    </Anchor>
                  )}
                  {canChangeDate && sessionData.status === 'scheduled' && !needsAttendance && (
                    <Anchor
                      component="button"
                      type="button"
                      size="sm"
                      c="red"
                      onClick={handleCancelSession}
                    >
                      Cancel session
                    </Anchor>
                  )}
                </Group>
              )}
              {/* F8: an undated 1:1 (for example after a no-show) is dated from here. */}
              {sessionData && !sessionData.scheduled_start && canChangeDate && (
                missBadge ? (
                  // MED-1346: after a miss, a button, so the next step stands out.
                  <Button
                    size="xs"
                    variant="outline"
                    color="medleyGreen"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => onEditDate!(fullSession!)}
                  >
                    + Add Date
                  </Button>
                ) : (
                  <Anchor
                    component="button"
                    type="button"
                    size="sm"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => onEditDate!(fullSession!)}
                  >
                    + Add Date
                  </Anchor>
                )
              )}
              <Group gap={8} align="center">
                <Badge
                  size="sm"
                  color={statusConfig.color}
                  variant="light"
                  style={{ textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: 10 }}
                >
                  {statusConfig.label}
                </Badge>
                {missBadge && (
                  <MissTagTooltip description={missBadge.description} width={240}>
                    <Badge
                      size="sm"
                      color={missBadge.color}
                      variant="light"
                      tabIndex={0}
                      aria-label={`${missBadge.label}. ${missBadge.description}`}
                      style={{ textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: 10 }}
                    >
                      {missBadge.label}
                    </Badge>
                  </MissTagTooltip>
                )}
                {/* MED-1346: staff only; the coach view has no Data Library. A visible
                    link, so touch and keyboard users reach it too. */}
                {!isCoachView && historyParticipantId && (
                  <Anchor component={Link} to={oneOnOneHistoryHref(cohortId, historyParticipantId)} size="xs">
                    See 1:1 history
                  </Anchor>
                )}
                {zoomLink && (
                  <Text
                    size="xs"
                    c="blue"
                    component="a"
                    href={zoomLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <Video size={12} />
                    Join Meeting
                  </Text>
                )}
                {/* MED-1346: a filled button, so a pending claim reads as a task. */}
                {canAnswerClaim && (
                  <Button
                    size="compact-xs"
                    onClick={() => openMissClaimQuestion(pendingClaim!, handleComplete, !isCoachView)}
                  >
                    Answer
                  </Button>
                )}
                {canFlagLateChange && (
                  <Anchor component="button" type="button" size="xs" onClick={() => setFlagOpened(true)}>
                    Flag late change
                  </Anchor>
                )}
              </Group>
            </Stack>

            {/* Center: participant + coach chips */}
            <Group gap="xl" style={{ minWidth: 0, rowGap: 8 }}>
              {/* Participant */}
              <Group gap="sm" wrap="nowrap">
                <Avatar src={participantAvatarUrl} size={40} radius="xl" color="blue">
                  {getInitials(participantName)}
                </Avatar>
                <Box>
                  <Text size="10px" c="dimmed" fw={600} style={{ letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                    Participant
                  </Text>
                  <Text size="13px" fw={500}>{participantName}</Text>
                </Box>
              </Group>

              {/* Coach */}
              {headerCoachName ? (
                <Group gap="sm" wrap="nowrap">
                  <Avatar src={headerCoachAvatarUrl} size={40} radius="xl" color="medleyGreen">
                    {getInitials(headerCoachName)}
                  </Avatar>
                  <Box>
                    <Text size="10px" c="dimmed" fw={600} style={{ letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                      Coach
                    </Text>
                    <Text size="13px" fw={500}>{headerCoachName}</Text>
                  </Box>
                </Group>
              ) : (
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon size={40} radius="xl" variant="light" color="orange">
                    <Text size="xs">?</Text>
                  </ThemeIcon>
                  <Box>
                    <Text size="10px" c="dimmed" fw={600} style={{ letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                      Coach
                    </Text>
                    <Text size="13px" c="dimmed">Unassigned</Text>
                  </Box>
                </Group>
              )}
            </Group>
          </Group>

          <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label="Close">
            <X size={18} />
          </ActionIcon>
        </Group>
      </Box>

      <Tabs
        value={shownTab}
        onChange={setActiveTab}
        className={styles.tabsContainer}
      >
        <Tabs.List className={styles.tabsList}>
          {showPreSession && (
            <Tabs.Tab value="pre_session" leftSection={<Calendar size={14} />}>
              Pre Session
            </Tabs.Tab>
          )}
          <Tabs.Tab value="post_session" leftSection={<Clipboard size={14} />}>
            Post Session
          </Tabs.Tab>
          <Tabs.Tab
            value="notes"
            leftSection={<FileText size={14} />}
            rightSection={
              notesCount > 0 ? (
                <Badge size="xs" variant="filled" color="gray" circle>
                  {notesCount}
                </Badge>
              ) : undefined
            }
          >
            Internal Notes
          </Tabs.Tab>
        </Tabs.List>

        <ScrollArea className={styles.tabContent}>
          <div style={{ padding: 'var(--mantine-spacing-md)' }}>
            <Tabs.Panel value="pre_session">
              <PreSessionTab
                cohortId={cohortId}
                sessionId={sessionId}
                sessions={[]}
                categories={categories}
                readOnly={resourcesReadOnly}
                hideRsvps
                onResourceCountChange={setResourceCount}
              />
            </Tabs.Panel>

            <Tabs.Panel value="post_session">
              {/* 1:1 Post Session: Attendance + Coach Feedback only — no Participant Feedback */}
              <Stack gap="sm">
                {/*
                  MED-696 AC6a: Form header shows session type so coaches
                  submitting a 360 / Hogan / other debrief see the correct
                  context. typeConfig.fullLabel comes from oneOnOneTypeConfig.
                  AC6b: Debrief variants label the notes accordion "Debrief
                  Notes" so non-session recaps aren't mislabeled.
                */}
                <Text fw={700} size="md" style={{ lineHeight: 1.2 }}>
                  Session Recap · {typeConfig.fullLabel}
                </Text>
                <SectionAccordion title="Attendance" defaultExpanded>
                  {undated ? (
                    <Text size="sm" c="dimmed">
                      Add a date to this 1:1 to mark attendance.
                    </Text>
                  ) : !ownerKnown ? (
                    <Center py="md">
                      <Loader size="sm" />
                    </Center>
                  ) : viewerActs || sessionLoadFailed ? (
                    <AttendanceSection
                      cohortId={cohortId}
                      sessionId={sessionId}
                      onComplete={handleComplete}
                      oneOnOne
                      // MED-1346: a check-in never pays a no-show.
                      noShowPay={
                        sessionData && !isCheckin
                          ? { paidMissCount: missClaims?.paidMissCount ?? null }
                          : undefined
                      }
                    />
                  ) : (
                    <Text size="sm" c="dimmed">
                      Attendance is marked by the session coach.
                    </Text>
                  )}
                </SectionAccordion>
                {viewerActs && (
                  <SectionAccordion
                    title={typeConfig.isDebrief ? 'Debrief Notes' : 'Session Notes'}
                    defaultExpanded={false}
                  >
                    <CoachFeedbackSection
                      cohortId={cohortId}
                      sessionId={sessionId}
                      onFlagAddressed={handleComplete}
                    />
                  </SectionAccordion>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="notes">
              <InternalNotesSection
                cohortId={cohortId}
                sessionId={sessionId}
                onNotesChanged={handleNotesChanged}
                readOnly={!viewerActs}
              />
            </Tabs.Panel>
          </div>
        </ScrollArea>
      </Tabs>

      {canFlagLateChange && (
        <FlagLateChangeModal
          opened={flagOpened}
          onClose={() => setFlagOpened(false)}
          cohortId={cohortId}
          sessionId={sessionId}
          participantName={participantName}
          scheduledStart={sessionData?.scheduled_start ?? null}
          onFlagged={handleComplete}
        />
      )}
    </Drawer>
  );
}

export default OneOnOneSessionDetailDrawer;
